/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import {
  isDraftTeam,
  startTeamImageUrl,
} from '@sportninja/common/features/registration/startStep/teamDisplay'
import type {
  StartRole,
  StartTeam,
} from '@sportninja/common/features/registration/startStep/types'
import { BUCKET_IMAGE_SRC } from 'src/components/ts-components/icons/bucketImage'
import Text from 'src/components/ts-components/Text'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface Props {
  /** Names the label: a rep registers a team, a player registers themselves. */
  role?: StartRole | null
  /** The team they picked, when they picked one. */
  team?: StartTeam | null
  /** The division the reader picked, or the one their team belongs to. */
  divisionName?: string | null
}

/**
 * What the reader is about to register for, stated beside the Next button.
 *
 * The footer is otherwise an action with no subject: by the time it appears the
 * reader may have scrolled the grid well out of view, and a selected card they
 * cannot see is not an answer they can check. This restates it at the moment of
 * committing.
 *
 * Renders nothing when there is nothing to name, so it cannot leave an empty
 * column propping the footer open.
 */
const SelectionSummary = ({ role, team, divisionName }: Props) => {
  const teamName = team?.name
  if (!divisionName && !teamName) {
    return null
  }

  // The same three-way the team cards use: a draft has no crest of its own and
  // takes the shared bucket image, a team without one falls back to the
  // placeholder, and a real image is resolved to the active CDN host.
  const logo = team
    ? isDraftTeam(team)
      ? BUCKET_IMAGE_SRC
      : resolveAssetUrl(startTeamImageUrl(team)) || TEAM_PLACEHOLDER
    : null

  return (
    <div css={wrapStyle}>
      <Text
        variant='paragraphXSmall'
        weight='regular'
        color={colors.NEUTRAL_50}
      >
        {role === 'team' ? 'Team Registration For' : 'Player Registration For'}
      </Text>

      <div css={valuesStyle}>
        {logo ? <img css={logoStyle} src={logo} alt='' /> : null}
        {teamName ? (
          <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
            {teamName}
          </Text>
        ) : null}
        {/* Only between two values, and hidden from screen readers — the names
            either side already read as separate nodes. */}
        {teamName && divisionName ? (
          <span aria-hidden='true'>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              ·
            </Text>
          </span>
        ) : null}
        {divisionName ? (
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={teamName ? colors.NEUTRAL_50 : colors.WHITE}
          >
            {divisionName}
          </Text>
        ) : null}
      </div>
    </div>
  )
}

export default SelectionSummary

/* Left-hand item of the footer row. `min-width: 0` so a long team name wraps
   rather than pushing the button off the line. */
const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  text-align: left;
`

/* Logo, team, division on one line. They wrap together rather than truncating
   mid-word, so a long pair drops the division under the team. */
const valuesStyle = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`

/* Sized to the paragraphSmall line beside it, with the same 8px radius and
   NEUTRAL_700 well the team cards give their crests. */
const logoStyle = css`
  width: 24px;
  height: 24px;
  flex: none;
  border-radius: 8px;
  object-fit: contain;
  background-color: ${colors.NEUTRAL_700};
`
